import React from "react";

// Function to format page names
const formatPageName = (eventTitle) => {
  if (!eventTitle) return "Unknown Page";

  // Remove "MOVED_TO_" prefix and "_PAGE" suffix
  let pageName = eventTitle
    .replace(/^MOVED_TO_/, "")
    .replace(/_PAGE$/, "")
    .replace(/_/g, " ");

  // Convert to title case
  pageName = pageName
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return pageName;
};

// Function to get color based on rank
const getRankColor = (index) => {
  const colors = [
    "#667eea", // Purple
    "#48bb78", // Green
    "#f59e0b", // Orange
    "#ec4899", // Pink
    "#06b6d4", // Cyan
    "#8b5cf6", // Violet
    "#10b981", // Emerald
    "#6366f1", // Indigo
  ];
  return colors[index] || "#94a3b8"; // Default gray for others
};

// Component to render Popular Pages List
const PopularPagesList = ({ data, interval }) => {
  // Format data: [[event_title, event_count, unique_users, percentage], ...]
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "200px",
          color: "#a0aec0",
          fontSize: "14px",
        }}
      >
        📊 No data available
        <small style={{ fontSize: "11px", color: "#cbd5e0", marginTop: "8px" }}>
          Try selecting a different date range
        </small>
      </div>
    );
  }

  const formattedData = data.map((item, index) => ({
    rank: index + 1,
    pageName: formatPageName(item[0]),
    eventCount: item[1],
    uniqueUsers: item[2],
    percentage: item[3],
    color: getRankColor(index),
  }));

  // Find max count for bar width calculation
  const maxCount = Math.max(...formattedData.map((item) => item.eventCount));

  return (
    <div
      style={{
        width: "100%",
        padding: "8px 0",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "40px 1fr 100px 100px 70px",
          gap: "12px",
          padding: "8px 12px",
          borderBottom: "2px solid #e2e8f0",
          fontSize: "11px",
          fontWeight: "700",
          color: "#718096",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        <div>Rank</div>
        <div>Page</div>
        <div style={{ textAlign: "right" }}>Visits</div>
        <div style={{ textAlign: "right" }}>Users</div>
        <div style={{ textAlign: "right" }}>%</div>
      </div>

      {/* List Items */}
      <div style={{ maxHeight: "400px", overflowY: "auto" }}>
        {formattedData.map((item) => (
          <div
            key={item.rank}
            style={{
              display: "grid",
              gridTemplateColumns: "40px 1fr 100px 100px 70px",
              gap: "12px",
              padding: "12px",
              borderBottom: "1px solid #f7fafc",
              alignItems: "center",
              transition: "all 0.2s",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f7fafc";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            {/* Rank */}
            <div
              style={{
                width: "28px",
                height: "28px",
                borderRadius: "50%",
                backgroundColor: item.color,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "13px",
                fontWeight: "700",
              }}
            >
              {item.rank}
            </div>

            {/* Page Name with Progress Bar */}
            <div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#2d3748",
                  marginBottom: "4px",
                }}
              >
                {item.pageName}
              </div>
              <div
                style={{
                  width: "100%",
                  height: "6px",
                  backgroundColor: "#e2e8f0",
                  borderRadius: "3px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${(item.eventCount / maxCount) * 100}%`,
                    height: "100%",
                    backgroundColor: item.color,
                    borderRadius: "3px",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </div>

            {/* Event Count */}
            <div
              style={{
                textAlign: "right",
                fontSize: "14px",
                fontWeight: "600",
                color: "#2d3748",
              }}
            >
              {item.eventCount.toLocaleString()}
            </div>

            {/* Unique Users */}
            <div
              style={{
                textAlign: "right",
                fontSize: "14px",
                fontWeight: "600",
                color: "#718096",
              }}
            >
              {item.uniqueUsers.toLocaleString()}
            </div>

            {/* Percentage */}
            <div
              style={{
                textAlign: "right",
                fontSize: "13px",
                fontWeight: "700",
                color: item.color,
              }}
            >
              {item.percentage.toFixed(1)}%
            </div>
          </div>
        ))}
      </div>

      {/* Footer Summary */}
      <div
        style={{
          marginTop: "12px",
          padding: "12px",
          backgroundColor: "#f7fafc",
          borderRadius: "8px",
          display: "flex",
          justifyContent: "space-between",
          fontSize: "12px",
          color: "#718096",
        }}
      >
        <div>
          <strong style={{ color: "#2d3748" }}>Total Pages:</strong>{" "}
          {formattedData.length}
        </div>
        <div>
          <strong style={{ color: "#2d3748" }}>Total Visits:</strong>{" "}
          {formattedData
            .reduce((sum, item) => sum + item.eventCount, 0)
            .toLocaleString()}
        </div>
        <div>
          <strong style={{ color: "#2d3748" }}>Unique Users:</strong>{" "}
          {formattedData
            .reduce((sum, item) => sum + item.uniqueUsers, 0)
            .toLocaleString()}
        </div>
      </div>
    </div>
  );
};

export default PopularPagesList;
